import { useEffect, useRef } from 'react'
import { forcyteService } from '@/services'
import { useRefreshToken } from '@/hooks'
import {
  isTokenValid,
  shouldRefreshToken,
  getTimeUntilExpiry,
} from '@/utils/auth'

export const useTokenRefresh = donor => {
  const { refreshToken } = useRefreshToken()
  const refreshTimeoutRef = useRef(null)
  const refreshTokenRef = useRef(refreshToken)

  useEffect(() => {
    refreshTokenRef.current = refreshToken
  }, [refreshToken])

  useEffect(() => {
    const isLoggedIn = donor?.id

    const scheduleTokenRefresh = () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }

      const timeUntilExpiry = getTimeUntilExpiry()
      if (timeUntilExpiry <= 0) {
        return
      }

      const twoMinutesInMs = 2 * 60 * 1000
      const timeUntilRefresh = Math.max(timeUntilExpiry - twoMinutesInMs, 0)

      const timeInMinutes = (timeUntilRefresh / 1000 / 60).toFixed(2)
      const expiryDate = new Date(Date.now() + timeUntilExpiry)

      console.log(`🔄 Token refresh scheduled in ${timeInMinutes} minutes`)

      refreshTimeoutRef.current = setTimeout(() => {
        handleTokenRefresh()
      }, timeUntilRefresh)
    }

    const handleTokenRefresh = async () => {
      try {
        const response = await refreshTokenRef.current()

        if (response?.token && response?.token_expiry) {
          forcyteService.setToken(response.token, response.token_expiry)

          scheduleTokenRefresh()
        } else {
          console.error('Invalid refresh response:', response)
        }
      } catch (error) {
        console.error('❌ Failed to refresh token:', error)
        forcyteService.clearToken()
        forcyteService.refresh()
      }
    }

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }

    if (!isLoggedIn) return

    const initializeAuth = () => {
      const hasToken = forcyteService.getToken()

      if (!hasToken) return

      if (!isTokenValid()) {
        forcyteService.clearToken()
        forcyteService.refresh()
        return
      }

      if (shouldRefreshToken()) {
        handleTokenRefresh()
      } else {
        scheduleTokenRefresh()
      }
    }

    initializeAuth()

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [donor?.id])
}
